/**
 * MetadataEditor Component
 *
 * A comprehensive metadata editor for audio/video content with tabbed interface
 * inspired by Apple Music's Get Info dialog.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  Typography,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip,
  Rating,
  Divider,
  Alert,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Close,
  MusicNote,
  NavigateBefore,
  NavigateNext,
  Save,
  Refresh,
  LibraryMusic,
  Fingerprint,
  GraphicEq,
  KeyboardArrowDown,
  CloudUpload,
  Delete,
} from '@mui/icons-material';
import type { Content } from '../../api/types';
import { TagService, ContentService, type Tag } from '../../api';
import TagSelector from './TagSelector';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`metadata-tabpanel-${index}`}
      aria-labelledby={`metadata-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Genre options
const GENRES = [
  'Pop', 'Rock', 'Jazz', 'Classical', 'Electronic', 'Hip-Hop', 'R&B', 'Country',
  'Folk', 'Blues', 'Reggae', 'Latin', 'World', 'Ambient', 'Soundtrack', 'Other'
];

type ReprocessMode = 'id3' | 'identify' | 'audio' | 'full';

interface MetadataEditorProps {
  open: boolean;
  onClose: () => void;
  content: Content | null;
  onSave: (id: string, metadata: Partial<ContentMetadata>) => Promise<void>;
  onReprocess?: (id: string, mode: ReprocessMode) => Promise<void>;
  onNavigate?: (direction: 'prev' | 'next') => void;
  canNavigatePrev?: boolean;
  canNavigateNext?: boolean;
}

interface ContentMetadata {
  title: string;
  artist: string;
  album: string;
  album_artist: string;
  composer: string;
  grouping: string;
  genre: string;
  year: number | null;
  track_number: number | null;
  track_total: number | null;
  disc_number: number | null;
  disc_total: number | null;
  bpm: number | null;
  rating: number;
  comments: string;
  lyrics: string;
  is_compilation: boolean;
  is_explicit: boolean;
  // Playback options
  start_time: string;
  stop_time: string;
  volume_adjustment: number;
  skip_when_shuffling: boolean;
  // Sorting
  sort_title: string;
  sort_artist: string;
  sort_album: string;
  sort_album_artist: string;
  sort_composer: string;
}

export default function MetadataEditor({
  open,
  onClose,
  content,
  onSave,
  onReprocess,
  onNavigate,
  canNavigatePrev = false,
  canNavigateNext = false,
}: MetadataEditorProps) {
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Reprocess state
  const [reprocessAnchorEl, setReprocessAnchorEl] = useState<null | HTMLElement>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Tags state
  const [contentTags, setContentTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Artwork state
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const artworkInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [metadata, setMetadata] = useState<ContentMetadata>({
    title: '',
    artist: '',
    album: '',
    album_artist: '',
    composer: '',
    grouping: '',
    genre: '',
    year: null,
    track_number: null,
    track_total: null,
    disc_number: null,
    disc_total: null,
    bpm: null,
    rating: 0,
    comments: '',
    lyrics: '',
    is_compilation: false,
    is_explicit: false,
    start_time: '0:00',
    stop_time: '',
    volume_adjustment: 0,
    skip_when_shuffling: false,
    sort_title: '',
    sort_artist: '',
    sort_album: '',
    sort_album_artist: '',
    sort_composer: '',
  });

  // Initialize form when content changes
  useEffect(() => {
    if (content) {
      setMetadata({
        title: content.title || '',
        artist: content.artist || '',
        album: content.album || '',
        album_artist: content.album_artist || '',
        composer: content.composer || '',
        grouping: content.grouping || '',
        genre: content.genre || '',
        year: content.year || null,
        track_number: content.track_number || null,
        track_total: content.track_total || null,
        disc_number: content.disc_number || null,
        disc_total: content.disc_total || null,
        bpm: content.bpm || null,
        rating: content.rating || 0,
        comments: content.comments || '',
        lyrics: content.lyrics || '',
        is_compilation: content.is_compilation || false,
        is_explicit: content.is_explicit || false,
        start_time: '0:00',
        stop_time: formatDuration(content.duration || 0),
        volume_adjustment: content.volume_adjustment || 0,
        skip_when_shuffling: content.skip_when_shuffling || false,
        sort_title: content.sort_title || content.title || '',
        sort_artist: content.sort_artist || content.artist || '',
        sort_album: content.sort_album || content.album || '',
        sort_album_artist: content.sort_album_artist || content.album_artist || '',
        sort_composer: content.sort_composer || content.composer || '',
      });
      setHasChanges(false);
      setError(null);

      // Load content tags
      const loadTags = async () => {
        setLoadingTags(true);
        try {
          const tags = await TagService.getContentTags(content.id);
          setContentTags(tags);
        } catch (err) {
          console.error('Failed to load tags:', err);
        } finally {
          setLoadingTags(false);
        }
      };
      loadTags();

      // Set artwork URL if available
      if (content.artwork_r2_key) {
        setArtworkUrl(ContentService.getArtworkUrl(content.id));
      } else {
        setArtworkUrl(null);
      }
    }
  }, [content]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleChange = (field: keyof ContentMetadata, value: any) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!content) return;

    setSaving(true);
    setError(null);

    try {
      await onSave(content.id, metadata);
      setHasChanges(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save metadata');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      // Could add a confirmation dialog here
    }
    onClose();
  };

  const handleReprocessClick = (event: React.MouseEvent<HTMLElement>) => {
    setReprocessAnchorEl(event.currentTarget);
  };

  const handleReprocessClose = () => {
    setReprocessAnchorEl(null);
  };

  const handleReprocess = async (mode: ReprocessMode) => {
    if (!content || !onReprocess) return;

    handleReprocessClose();
    setReprocessing(true);

    try {
      await onReprocess(content.id, mode);
      const modeLabels: Record<ReprocessMode, string> = {
        id3: 'ID3 tags extraction',
        identify: 'Track identification',
        audio: 'Audio analysis',
        full: 'Full analysis',
      };
      setSnackbarMessage(`${modeLabels[mode]} queued successfully`);
      setSnackbarOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to start reprocessing');
    } finally {
      setReprocessing(false);
    }
  };

  // Artwork handlers
  const handleArtworkClick = () => {
    artworkInputRef.current?.click();
  };

  const handleArtworkSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!content || !event.target.files?.length) return;

    const file = event.target.files[0];
    setUploadingArtwork(true);
    setError(null);

    try {
      await ContentService.uploadArtwork(content.id, file);
      // Update artwork URL with cache buster
      setArtworkUrl(`${ContentService.getArtworkUrl(content.id)}?t=${Date.now()}`);
      setSnackbarMessage('Artwork uploaded successfully');
      setSnackbarOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to upload artwork');
    } finally {
      setUploadingArtwork(false);
      // Reset input so same file can be selected again
      if (artworkInputRef.current) {
        artworkInputRef.current.value = '';
      }
    }
  };

  const handleArtworkDelete = async () => {
    if (!content) return;

    setUploadingArtwork(true);
    setError(null);

    try {
      await ContentService.deleteArtwork(content.id);
      setArtworkUrl(null);
      setSnackbarMessage('Artwork deleted');
      setSnackbarOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to delete artwork');
    } finally {
      setUploadingArtwork(false);
    }
  };

  if (!content) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02), rgba(255,255,255,0))',
          minHeight: '600px',
        }
      }}
    >
      {/* Header with artwork and title */}
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Album artwork placeholder */}
          <Box
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'action.hover',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid',
              borderColor: 'primary.main',
              flexShrink: 0,
            }}
          >
            {content.artwork_url ? (
              <img
                src={content.artwork_url}
                alt="Artwork"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
              />
            ) : (
              <MusicNote sx={{ fontSize: 40, color: 'text.secondary' }} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {metadata.title || content.original_filename}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {metadata.artist || 'Unknown Artist'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {metadata.album || 'Unknown Album'}
            </Typography>
          </Box>

          {/* Reprocess dropdown button */}
          {onReprocess && (
            <Tooltip title="Rescan file for metadata">
              <Button
                size="small"
                variant="outlined"
                onClick={handleReprocessClick}
                disabled={reprocessing}
                endIcon={reprocessing ? <CircularProgress size={16} /> : <KeyboardArrowDown />}
                startIcon={<Refresh />}
                sx={{ ml: 2, textTransform: 'none' }}
              >
                {reprocessing ? 'Processing...' : 'Reprocess'}
              </Button>
            </Tooltip>
          )}

          {/* Reprocess menu */}
          <Menu
            anchorEl={reprocessAnchorEl}
            open={Boolean(reprocessAnchorEl)}
            onClose={handleReprocessClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleReprocess('id3')}>
              <ListItemIcon>
                <LibraryMusic fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Re-read ID3 Tags"
                secondary="Fast, extracts embedded metadata"
              />
            </MenuItem>
            <MenuItem onClick={() => handleReprocess('identify')}>
              <ListItemIcon>
                <Fingerprint fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Identify Track"
                secondary="ACRCloud recognition (uses quota)"
              />
            </MenuItem>
            <MenuItem onClick={() => handleReprocess('audio')}>
              <ListItemIcon>
                <GraphicEq fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Rescan Audio"
                secondary="Bitrate, loudness, energy analysis"
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleReprocess('full')}>
              <ListItemIcon>
                <Refresh fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Full Analysis"
                secondary="All of the above"
              />
            </MenuItem>
          </Menu>

          <IconButton onClick={handleClose} sx={{ ml: 1 }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Details" />
            <Tab label="Tags" />
            <Tab label="Artwork" />
            <Tab label="Lyrics" />
            <Tab label="Options" />
            <Tab label="Sorting" />
            <Tab label="File" />
          </Tabs>
        </Box>

        {/* Details Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={metadata.title}
                onChange={(e) => handleChange('title', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Artist"
                value={metadata.artist}
                onChange={(e) => handleChange('artist', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Album"
                value={metadata.album}
                onChange={(e) => handleChange('album', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Album Artist"
                value={metadata.album_artist}
                onChange={(e) => handleChange('album_artist', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Composer"
                value={metadata.composer}
                onChange={(e) => handleChange('composer', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Grouping"
                value={metadata.grouping}
                onChange={(e) => handleChange('grouping', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Genre</InputLabel>
                <Select
                  value={metadata.genre}
                  label="Genre"
                  onChange={(e) => handleChange('genre', e.target.value)}
                >
                  {GENRES.map((genre) => (
                    <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={metadata.year || ''}
                onChange={(e) => handleChange('year', e.target.value ? parseInt(e.target.value) : null)}
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Track"
                type="number"
                value={metadata.track_number || ''}
                onChange={(e) => handleChange('track_number', e.target.value ? parseInt(e.target.value) : null)}
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="of"
                type="number"
                value={metadata.track_total || ''}
                onChange={(e) => handleChange('track_total', e.target.value ? parseInt(e.target.value) : null)}
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Disc"
                type="number"
                value={metadata.disc_number || ''}
                onChange={(e) => handleChange('disc_number', e.target.value ? parseInt(e.target.value) : null)}
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="of"
                type="number"
                value={metadata.disc_total || ''}
                onChange={(e) => handleChange('disc_total', e.target.value ? parseInt(e.target.value) : null)}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="BPM"
                type="number"
                value={metadata.bpm || ''}
                onChange={(e) => handleChange('bpm', e.target.value ? parseInt(e.target.value) : null)}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Rating
                </Typography>
                <Rating
                  value={metadata.rating}
                  onChange={(_, newValue) => handleChange('rating', newValue || 0)}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments"
                value={metadata.comments}
                onChange={(e) => handleChange('comments', e.target.value)}
                multiline
                rows={2}
                size="small"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tags Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Content Tags
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add tags to organize and categorize this content for playlists and filtering.
            </Typography>

            {loadingTags ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading tags...
                </Typography>
              </Box>
            ) : content ? (
              <TagSelector
                contentId={content.id}
                selectedTags={contentTags}
                onTagsChange={setContentTags}
                showSuggestions={true}
              />
            ) : null}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Tag Categories
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tags are organized by category to help you find the right ones quickly:
            </Typography>
            <Grid container spacing={1}>
              {['Mood', 'Tempo', 'Era', 'Use', 'Vocal', 'Energy', 'Style', 'Season', 'Type'].map(category => (
                <Grid item key={category}>
                  <Chip label={category} size="small" variant="outlined" />
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Artwork Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {/* Hidden file input */}
            <input
              type="file"
              ref={artworkInputRef}
              onChange={handleArtworkSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />

            <Box
              sx={{
                width: 200,
                height: 200,
                bgcolor: 'action.hover',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                border: '2px dashed',
                borderColor: artworkUrl ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
                position: 'relative',
                overflow: 'hidden',
              }}
              onClick={handleArtworkClick}
            >
              {uploadingArtwork ? (
                <CircularProgress size={40} />
              ) : artworkUrl ? (
                <img
                  src={artworkUrl}
                  alt="Album Artwork"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                  onError={() => setArtworkUrl(null)}
                />
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Click to upload
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Album Artwork
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Supported: JPG, PNG, GIF, WebP (max 5MB)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleArtworkClick}
                disabled={uploadingArtwork}
                startIcon={<CloudUpload />}
              >
                {artworkUrl ? 'Replace' : 'Upload'}
              </Button>
              {artworkUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleArtworkDelete}
                  disabled={uploadingArtwork}
                  startIcon={<Delete />}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* Lyrics Tab */}
        <TabPanel value={tabValue} index={3}>
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder="Enter lyrics..."
            value={metadata.lyrics}
            onChange={(e) => handleChange('lyrics', e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
              }
            }}
          />
        </TabPanel>

        {/* Options Tab */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Media Kind
              </Typography>
              <Chip label="Music" variant="outlined" size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Time"
                value={metadata.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                size="small"
                helperText="Format: m:ss"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Stop Time"
                value={metadata.stop_time}
                onChange={(e) => handleChange('stop_time', e.target.value)}
                size="small"
                helperText="Format: m:ss"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Volume Adjustment
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={metadata.volume_adjustment}
                  onChange={(_, value) => handleChange('volume_adjustment', value)}
                  min={-100}
                  max={100}
                  marks={[
                    { value: -100, label: '-100%' },
                    { value: 0, label: 'None' },
                    { value: 100, label: '+100%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}%`}
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Sorting Tab */}
        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Title"
                value={metadata.title}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sort As"
                value={metadata.sort_title}
                onChange={(e) => handleChange('sort_title', e.target.value)}
                size="small"
                placeholder={metadata.title}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Album"
                value={metadata.album}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sort As"
                value={metadata.sort_album}
                onChange={(e) => handleChange('sort_album', e.target.value)}
                size="small"
                placeholder={metadata.album}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Artist"
                value={metadata.artist}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sort As"
                value={metadata.sort_artist}
                onChange={(e) => handleChange('sort_artist', e.target.value)}
                size="small"
                placeholder={metadata.artist}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Composer"
                value={metadata.composer}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sort As"
                value={metadata.sort_composer}
                onChange={(e) => handleChange('sort_composer', e.target.value)}
                size="small"
                placeholder={metadata.composer}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* File Tab */}
        <TabPanel value={tabValue} index={6}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Duration</Typography>
              <Typography variant="body1">{formatDuration(content.duration || 0)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Size</Typography>
              <Typography variant="body1">{formatFileSize(content.file_size || 0)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Format</Typography>
              <Typography variant="body1">{content.mime_type || 'Unknown'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Date Added</Typography>
              <Typography variant="body1">
                {content.created_at ? new Date(content.created_at).toLocaleDateString() : 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Last Modified</Typography>
              <Typography variant="body1">
                {content.updated_at ? new Date(content.updated_at).toLocaleDateString() : 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Filename</Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                {content.original_filename || content.filename}
              </Typography>
            </Grid>
            {content.copyright && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Copyright</Typography>
                <Typography variant="body1">{content.copyright}</Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
          <IconButton
            size="small"
            disabled={!canNavigatePrev}
            onClick={() => onNavigate?.('prev')}
          >
            <NavigateBefore />
          </IconButton>
          <IconButton
            size="small"
            disabled={!canNavigateNext}
            onClick={() => onNavigate?.('next')}
          >
            <NavigateNext />
          </IconButton>
        </Box>

        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || !hasChanges}
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>

      {/* Success snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
}
